/**
 * Gating Utility Functions
 *
 * Reusable utilities for tier-gating logic.
 */

import { TIER_HIERARCHY, type UserTier } from './types';

/**
 * Checks if a user has access to content requiring a specific tier.
 *
 * Uses hierarchical tier system where higher tiers have access to lower-tier content.
 * Unknown tiers default to anonymous (tier 0) for security.
 *
 * @param userTier - Current user's tier (e.g., 'free', 'drama_plus')
 * @param requiredTier - Minimum tier required for access
 * @returns true if user tier >= required tier
 *
 * @example
 * hasTierAccess('drama_plus', 'drama_plus') // true
 * hasTierAccess('free', 'drama_plus') // false
 * hasTierAccess('dramax', 'drama_plus') // true (higher tier)
 */
export function hasTierAccess(userTier: string, requiredTier: UserTier): boolean {
  const userHierarchy = TIER_HIERARCHY[userTier as UserTier] ?? 0;
  const requiredHierarchy = TIER_HIERARCHY[requiredTier];
  return userHierarchy >= requiredHierarchy;
}
